/**
 * Research, Image and Result as chips, each opening a `WebMenu` of modes with
 * a one-line description; the chip carries a small state pill so the current
 * mode reads without opening anything.
 *
 * The chat composer no longer carries these: its "+" menu and "@" mentions
 * are the controls there (`composer-bar.web.tsx`). The Tasks composer and
 * builder keep the chips in their controlled mode, holding a task's own
 * picks.
 *
 * A capability this account cannot use is not shown at all. An image that
 * only needs a higher plan stays visible with a lock, and opens the upgrade
 * modal instead of a menu.
 */
import { useCallback, useRef, useState, type FC, type RefObject } from 'react';
import { usePathname } from 'expo-router';
import { useTranslation } from 'react-i18next';
import type { AgentCapabilityMode } from '@tabbio/types';
import { MaterialSymbol } from '@/components/icons/MaterialSymbol';
import { WebMenu, WebMenuModelOption } from '@/components/web/WebMenu';
import { useAgentCapabilities } from '@/hooks/useAgentCapabilities';
import {
  AGENT_CAPABILITY_MODES,
  applyAgentCapabilityChange,
  resolveTaskCapabilityControls,
  type AgentCapabilitiesPreference,
  type AgentCapabilityKey,
} from '@/lib/agent-capabilities-preference';
import { createChatUpgradeContext } from '@/lib/monetization-context';
import { useSubscriptionState } from '@/providers/useSubscriptionState';
import { useUpgradeModal } from '@/providers/UpgradeModalProvider';

type MenuKey = 'research' | 'image' | 'result';

const RESEARCH_ICON = 'language';
const IMAGE_ICON = 'image';
const RESULT_ICON = 'description';

function useImageUpgrade(entryPoint: string) {
  const { snapshot } = useSubscriptionState();
  const upgradeModal = useUpgradeModal();
  const pathname = usePathname();

  return useCallback(() => {
    upgradeModal.open(
      createChatUpgradeContext({
        entryPoint,
        planSet: snapshot?.planSet,
        recommendedTier: 'plus',
        returnTo: pathname,
      }),
    );
  }, [entryPoint, pathname, snapshot?.planSet, upgradeModal]);
}

const CapabilityChip: FC<{
  icon: string;
  label: string;
  stateLabel: string;
  tooltip: string;
  compact?: boolean;
  /** Anything other than Auto: the pill reads a little stronger. */
  isSet?: boolean;
  locked?: boolean;
  open?: boolean;
  buttonRef?: RefObject<HTMLButtonElement | null>;
  onClick: () => void;
}> = ({
  icon,
  label,
  stateLabel,
  tooltip,
  compact,
  isSet,
  locked,
  open,
  buttonRef,
  onClick,
}) => (
  <button
    ref={buttonRef}
    type="button"
    className={`tabbio-capability-chip${compact ? ' compact' : ''}${isSet ? ' is-set' : ''}`}
    onClick={onClick}
    aria-haspopup={locked ? 'dialog' : 'menu'}
    aria-expanded={locked ? undefined : Boolean(open)}
    aria-label={tooltip}
    title={tooltip}
  >
    <MaterialSymbol name={icon} className="tabbio-capability-chip-icon" />
    {compact ? null : <span className="tabbio-capability-chip-label">{label}</span>}
    {locked ? (
      <MaterialSymbol name="lock" className="tabbio-capability-chip-lock" />
    ) : (
      <span className="tabbio-capability-pill">{stateLabel}</span>
    )}
  </button>
);

/**
 * Desktop chips, rendered beside `<ModelPicker />`.
 *
 * Uncontrolled (chat): the picks are the sticky device preference every chat
 * composer reads. Controlled (`value` + `onChange`, the Tasks composer and
 * builder): the picks belong to the caller and the chat preference is never
 * written; the same availability rules and the Image and Result coupling
 * still apply, and the Result menu leaves out Page until Tasks can make pages
 * (a follow-up, see `TASK_ARTIFACT_MODES`).
 */
export const CapabilityChips: FC<{
  compact?: boolean;
  hidden?: boolean;
  value?: AgentCapabilitiesPreference;
  onChange?: (next: AgentCapabilitiesPreference) => void;
}> = ({ compact, hidden, value, onChange }) => {
  const { t } = useTranslation('common');
  const {
    capabilities,
    controls: stickyControls,
    setCapability: setStickyCapability,
  } = useAgentCapabilities();
  const controlled = value !== undefined && onChange !== undefined;
  const controls = controlled
    ? resolveTaskCapabilityControls(value, capabilities)
    : stickyControls;
  const setCapability = <K extends AgentCapabilityKey>(
    key: K,
    next: AgentCapabilitiesPreference[K],
  ) => {
    if (controlled) onChange(applyAgentCapabilityChange(value, key, next));
    else setStickyCapability(key, next);
  };
  const openImageUpgrade = useImageUpgrade('chat.capability_image');
  const [openMenu, setOpenMenu] = useState<MenuKey | null>(null);
  const researchRef = useRef<HTMLButtonElement | null>(null);
  const imageRef = useRef<HTMLButtonElement | null>(null);
  const resultRef = useRef<HTMLButtonElement | null>(null);
  const closeMenu = useCallback(() => setOpenMenu(null), []);
  const toggle = (key: MenuKey) =>
    setOpenMenu((current) => (current === key ? null : key));

  // Slash commands and employer threads ignore the controls server side, so
  // they are not offered there.
  if (hidden || !controls.anyVisible) return null;

  const stateLabel = (mode: AgentCapabilityMode) =>
    t(`chat.capabilities.states.${mode}`);
  const researchLabel = t('chat.capabilities.research.label');
  const imageLabel = t('chat.capabilities.image.label');
  const resultLabel = t('chat.capabilities.result.label');
  const resultState = t(
    `chat.capabilities.result.options.${controls.artifact.mode}.title`,
  );

  return (
    <>
      {controls.research.visible ? (
        <CapabilityChip
          buttonRef={researchRef}
          icon={RESEARCH_ICON}
          label={researchLabel}
          stateLabel={stateLabel(controls.research.mode)}
          tooltip={`${researchLabel}: ${stateLabel(controls.research.mode)}`}
          compact={compact}
          isSet={controls.research.mode !== 'auto'}
          open={openMenu === 'research'}
          onClick={() => toggle('research')}
        />
      ) : null}

      {controls.image.visible ? (
        <CapabilityChip
          buttonRef={imageRef}
          icon={IMAGE_ICON}
          label={imageLabel}
          stateLabel={stateLabel(controls.image.mode)}
          tooltip={
            controls.image.locked
              ? t('chat.capabilities.image.lockedHint')
              : `${imageLabel}: ${stateLabel(controls.image.mode)}`
          }
          compact={compact}
          locked={controls.image.locked}
          isSet={!controls.image.locked && controls.image.mode !== 'auto'}
          open={openMenu === 'image'}
          onClick={
            controls.image.locked ? openImageUpgrade : () => toggle('image')
          }
        />
      ) : null}

      {controls.artifact.visible ? (
        <CapabilityChip
          buttonRef={resultRef}
          icon={RESULT_ICON}
          label={resultLabel}
          stateLabel={resultState}
          tooltip={`${resultLabel}: ${resultState}`}
          compact={compact}
          isSet={controls.artifact.mode !== 'auto'}
          open={openMenu === 'result'}
          onClick={() => toggle('result')}
        />
      ) : null}

      <WebMenu
        open={openMenu === 'research'}
        onClose={closeMenu}
        anchor={researchRef.current}
        placement="top-start"
        minWidth={300}
        mobileSheet
      >
        <div className="tabbio-model-group-label">
          {t('chat.capabilities.research.sheetTitle')}
        </div>
        {AGENT_CAPABILITY_MODES.map((mode) => (
          <WebMenuModelOption
            key={mode}
            title={t(`chat.capabilities.research.options.${mode}.title`)}
            description={t(`chat.capabilities.research.options.${mode}.description`)}
            checked={controls.research.mode === mode}
            onSelect={() => setCapability('research', mode)}
          />
        ))}
      </WebMenu>

      <WebMenu
        open={openMenu === 'image'}
        onClose={closeMenu}
        anchor={imageRef.current}
        placement="top-start"
        minWidth={300}
        mobileSheet
      >
        <div className="tabbio-model-group-label">
          {t('chat.capabilities.image.sheetTitle')}
        </div>
        {AGENT_CAPABILITY_MODES.map((mode) => (
          <WebMenuModelOption
            key={mode}
            title={t(`chat.capabilities.image.options.${mode}.title`)}
            description={t(`chat.capabilities.image.options.${mode}.description`)}
            checked={controls.image.mode === mode}
            onSelect={() => setCapability('image', mode)}
          />
        ))}
      </WebMenu>

      <WebMenu
        open={openMenu === 'result'}
        onClose={closeMenu}
        anchor={resultRef.current}
        placement="top-start"
        minWidth={300}
        mobileSheet
      >
        <div className="tabbio-model-group-label">
          {t('chat.capabilities.result.sheetTitle')}
        </div>
        {controls.artifact.options.map((mode) => (
          <WebMenuModelOption
            key={mode}
            title={t(`chat.capabilities.result.options.${mode}.title`)}
            description={t(`chat.capabilities.result.options.${mode}.description`)}
            checked={controls.artifact.mode === mode}
            onSelect={() => setCapability('artifact', mode)}
          />
        ))}
      </WebMenu>
    </>
  );
};
